import { debugEvent, useEnvironment } from '@webng/react-app-common';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

export interface ChangeBridgeContextValue {
  ws: WebSocket | null;
  liveblogLocalId?: string;
  hasErrored: boolean;
  isConnected: boolean;
  setLiveblogLocalId: (liveblogLocalId: string) => void;
}

function createHeartbeatTimeoutFn(ws: WebSocket) {
  return () => {
    console.error("❌ No heartbeat received from server, closing connection");
    ws.close();
  }
}

function useChangeBridge(liveblogLocalId?: string): Omit<ChangeBridgeContextValue, "setLiveblogLocalId"> {

  const wsRef = useRef<WebSocket | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const realRetryCount = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const [hasErrored, setHasErrored] = useState(false);
  const env = useEnvironment();
  const abortControllerRef = useRef<AbortController | null>(null);

  const onVisibilityChange = () => {
    if (document.hidden) {
      setHasErrored(true);
      setRetryCount(0);
      realRetryCount.current = 0;
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounted");
        wsRef.current = null;
      }
    } else {
      setHasErrored(false);
    }
  }

  const onNetworkChange = () => {
    if (navigator.onLine) {
      setHasErrored(false);
    } else {
      setHasErrored(true);
      setRetryCount(0);
      realRetryCount.current = 0;
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounted");
        wsRef.current = null;
      }
    }
  }

  useEffect(() => {
    if (!liveblogLocalId) return;

    if(abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    document.addEventListener("visibilitychange", onVisibilityChange, false);
    window.addEventListener("online", onNetworkChange, false);
    window.addEventListener("offline", onNetworkChange, false);
    if (hasErrored) return;

    try {
      const apiHost = env.apiHost ? env.apiHost.replace("https://", "wss://") : "wss://staging.tickaroo.com"
      const ws = new WebSocket(`${apiHost}/changebridge/${liveblogLocalId}`);

      if(signal.aborted) {
        ws.close();
        return;
      }

      const heartbeat = setInterval(() => {
        if(signal.aborted) {
          clearInterval(heartbeat);
          return;
        }
        ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
      }, 20000)

      const heartbeatTimeoutFn = createHeartbeatTimeoutFn(ws);

      let heartbeatTimeout = setTimeout(heartbeatTimeoutFn, 60000)

      ws.addEventListener("open", () => {
        if(signal.aborted) {
          ws.close();
          return;
        }
        debugEvent("ws.onopen", {})
        realRetryCount.current = 0;
        setIsConnected(true);
      });

      ws.addEventListener("message", (event) => {
        if(signal.aborted) {
          return;
        }
        debugEvent("ws.onmessage", { data: event.data })
        const message = JSON.parse(event.data);
        if (message.type === "pong") {
          clearTimeout(heartbeatTimeout);
          heartbeatTimeout = setTimeout(heartbeatTimeoutFn, 60000)
          return;
        }
      });

      ws.addEventListener("close", (event) => {
        if(signal.aborted) {
          return;
        }
        debugEvent("ws.onclose", { code: event.code, reason: event.reason, wasClean: event.wasClean })
        setIsConnected(false);
        if (event.code !== 1000 && event.reason !== "Component unmounted") {
          console.error(`❌ WebSocket error: ${event.code} ${event.reason}`);
          if (realRetryCount.current >= 5) {
            console.error("❌ Too many retries, giving up");
            clearInterval(heartbeat);
            clearTimeout(heartbeatTimeout);
            setHasErrored(true);
          }
          setTimeout(() => {
            if(signal.aborted) {
              return;
            }
            setRetryCount((prev) => prev + 1);
            realRetryCount.current += 1;
          }, 5000);
        }
      });

      ws.addEventListener("error", (err) => {
        if(signal.aborted) {
          return;
        }
        console.error("⚠️ WebSocket error:", err);
        setIsConnected(false);
      });

      wsRef.current = ws;
      if(signal.aborted) {
        ws.close();
        return;
      }

      // Cleanup on unmount
      return () => {
        ws.close(1000, "Component unmounted");
        clearInterval(heartbeat);
        clearTimeout(heartbeatTimeout);

        if(abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }

        document.removeEventListener("visibilitychange", onVisibilityChange, false);
        window.removeEventListener("online", onNetworkChange, false);
        window.removeEventListener("offline", onNetworkChange, false);
      };
    } catch (error) {
      console.error("Error creating WebSocket:", error);
      // notifyErrorRaw(error) TODO: connect to analytics api
      setHasErrored(true);
      
      // Retry logic for WebSocket creation errors
      if (realRetryCount.current < 5) {
        setTimeout(() => {
          if(signal.aborted) {
            return;
          }
          setRetryCount((prev) => prev + 1);
          setHasErrored(false);
          realRetryCount.current += 1;
        }, 5000);
      }
    }
  }, [retryCount, liveblogLocalId, hasErrored, setHasErrored, env]);


  return {
    ws: wsRef.current,
    hasErrored,
    isConnected,
    liveblogLocalId
  };
}

const ChangeBridgeContext = createContext<ChangeBridgeContextValue | null>(null);

export const useChangeBridgeContext = () => useContext(ChangeBridgeContext);

export const ChangeBridgeProvider: React.FC<{ children: React.ReactNode, liveblogLocalId?: string, setLiveblogLocalId: (liveblogLocalId: string) => void }> = ({ children, liveblogLocalId, setLiveblogLocalId }) => {
  const changeBridgeValue = useChangeBridge(liveblogLocalId);

  return (
    <ChangeBridgeContext.Provider value={{...changeBridgeValue, setLiveblogLocalId}}>
      {children}
    </ChangeBridgeContext.Provider>
  );
};
